import React, {FC, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {Student, StudentSourceNameMap, StudentUpdateRequest} from '../../shared/types/student';
import {updateStudentFetch} from '../../app/axios/studentFetches';
import HLFormInput from '../../shared/components/input/HLFormInput';
import HLButton, {HLButtonStyleType} from '../../shared/components/button/HLButton';
import HLErrorMessage from '../../shared/components/error-message/HLErrorMessage';
import HLModal from '../../shared/components/modal/HLModal';
import {processFormServerError} from '../../shared/utils/processFormServerError';
import HLFormSelect from "../../shared/components/select/HLFormSelect";

interface UpdateStudentModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    student: Student | null;
    setStudent: (student: Student) => void;
}

const studentSourceOptions = Object.entries(StudentSourceNameMap).map(([value, label]) => ({
    value,
    label
}));

const UpdateStudentModal: FC<UpdateStudentModalProps> = (
    {
        open,
        setOpen,
        student,
        setStudent
    }) => {
    const {
        register,
        handleSubmit,
        reset,
        setError,
        setValue,
        watch,
        formState: {errors, isSubmitting}
    } = useForm<StudentUpdateRequest>();

    useEffect(() => {
        if (student && open) {
            setValue('firstName', student.firstName);
            setValue('lastName', student.lastName);
            setValue('telegram', student.telegram);
            setValue('email', student.email);
            setValue('source', student.source);
        }
    }, [student, open, setValue]);

    const updateStudent = async (request: StudentUpdateRequest) => {
        if (!student) return;

        const payload: StudentUpdateRequest = {
            firstName: request.firstName,
            source: request.source
        };
        if (request.lastName !== student.lastName) payload.lastName = request.lastName
        if (request.email !== student.email) payload.email = request.email
        if (request.telegram !== student.telegram) payload.telegram = request.telegram

        try {
            let updatedStudent = await updateStudentFetch(student.id, payload)
            setStudent(updatedStudent)
            closeModal()
        } catch (err: any) {
            processFormServerError(err, setError)
        }
    };

    const closeModal = () => {
        reset()
        setOpen(false)
    };

    if (!student) return null

    return (
        <HLModal open={open} onClose={closeModal} title="Изменить ученика">
            <form onSubmit={handleSubmit(updateStudent)} className="columnContainer">
                <HLErrorMessage error={errors.root?.message}/>

                <HLFormInput
                    id="firstName"
                    {...register('firstName', {required: 'Имя обязательно'})}
                    placeholder="Имя"
                    error={errors.firstName?.message}
                />

                <HLFormInput
                    id="lastName"
                    {...register('lastName')}
                    placeholder="Фамилия (необязательно)"
                    error={errors.lastName?.message}
                />

                <HLFormInput
                    id="telegram"
                    {...register('telegram')}
                    placeholder="Telegram (необязательно)"
                    error={errors.telegram?.message}
                />

                <HLFormInput
                    id="email"
                    {...register('email')}
                    placeholder="Email (необязательно)"
                    error={errors.email?.message}
                />

                <HLFormSelect
                    id="role"
                    {...register('source', {required: 'Источник обязателен'})}
                    value={watch('source') || ''}
                    options={[
                        {value: '', label: 'Откуда пришёл'},
                        ...studentSourceOptions
                    ]}
                    error={errors.source?.message}
                />

                <div className="inlineContainer">
                    <HLButton type="submit" disabled={isSubmitting}>
                        Изменить
                    </HLButton>
                    <HLButton type="button" onClick={closeModal} styleType={HLButtonStyleType.GRAY}>
                        Отмена
                    </HLButton>
                </div>
            </form>
        </HLModal>
    );
};

export default UpdateStudentModal; 